import { getState, requestTokenRefresh } from '../embed/host-bridge';
import type {
  Company,
  Invoice,
  Paginated,
  CertificateValidation,
  ReferenceItem,
  SalePointRef,
  HealthCheck,
  PadronHealthCheck,
} from './types';

// Cliente HTTP hacia el BFF. Nunca conoce las API keys del core: solo manda el Bearer token.
async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const { token, bffUrl } = getState();
  if (!token || !bffUrl) throw new Error('Sesión no inicializada');

  const res = await fetch(`${bffUrl}/bff/api/${path.replace(/^\/+/, '')}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    requestTokenRefresh();
    throw new Error('Sesión expirada, reintentá en un momento');
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = (data as any)?.message ?? `Error ${res.status}`;
    throw new Error(Array.isArray(message) ? message.join(', ') : message);
  }
  return data as T;
}

export const api = {
  // --- Consulta ---
  listInvoices: (params: Record<string, string | number> = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).reduce((a, [k, v]) => ({ ...a, [k]: String(v) }), {} as Record<string, string>),
    ).toString();
    return request<Paginated<Invoice>>('GET', `invoices${qs ? `?${qs}` : ''}`);
  },
  getInvoice: (urn: string) => request<Invoice>('GET', `invoices/${urn}`),
  downloadInvoice: (urn: string) => request<{ url: string }>('GET', `invoices/${urn}/download`),

  // --- Onboarding ---
  listCompanies: () => request<Company[]>('GET', 'companies'),
  getCompany: (urn: string) => request<Company>('GET', `companies/${urn}`),
  getCsr: (urn: string) => request<{ certificate: string; alias: string }>('GET', `companies/${urn}/certificate`),
  validateCertificate: (certificate: string) =>
    request<CertificateValidation>('POST', 'companies/validate/certificate', { certificate }),
  uploadArcaCertificate: (urn: string, certificate: string) =>
    request<Company>('PATCH', `companies/${urn}`, { certificates: { arca: { key: certificate } } }),
  createCompany: (payload: Record<string, unknown>) => request<Company>('POST', 'companies', payload),

  // --- Referencia ARCA ---
  salePoints: () => request<SalePointRef[]>('GET', 'invoices/arca/sale-points'),
  ivaConditionReceiver: () => request<ReferenceItem[]>('GET', 'invoices/arca/iva-condition-receiver'),
  documentTypes: () => request<ReferenceItem[]>('GET', 'invoices/arca/document-types'),
  ivaTypes: () => request<ReferenceItem[]>('GET', 'invoices/arca/iva-types'),
  healthCheck: () => request<HealthCheck>('GET', 'invoices/arca/health-check'),
  padronHealthCheck: () => request<PadronHealthCheck>('GET', 'invoices/arca/padron-health-check'),
};
